import { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import { PlusIcon, ScissorsIcon, TrashIcon } from '@phosphor-icons/react';
import { useSplitAPInvoiceLineItem } from '@/api/rpc/hooks/accounts-payable/useSplitAPInvoiceLineItem';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useToast } from '@/context/ToastContext';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils/formatters';
import {
  appendSplitPart,
  assessSplit,
  canAddSplitPart,
  initialSplitParts,
  toSplitPayload,
  unitPricePreview,
  type SplitPartDraft,
} from './splitLineItemForm';

export interface SplitLineItemTarget {
  id: string;
  lineNumber: number;
  description?: string | null;
  quantity?: string | number | null;
  totalPrice?: string | null;
}

/** Row action that opens the split dialog for one invoice line. */
export function SplitLineItemButton({
  invoiceId,
  lineItem,
  currency,
}: {
  invoiceId: string;
  lineItem: SplitLineItemTarget;
  currency: string;
}) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const label = gt('Split line {n}', {
    n: lineItem.lineNumber,
    context: 'AP invoice line items: split row action',
  });
  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            aria-label={label}
            onClick={() => setOpen(true)}
          >
            <ScissorsIcon className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      <SplitLineItemDialog
        invoiceId={invoiceId}
        lineItem={lineItem}
        currency={currency}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  );
}

/**
 * Split one invoice line into several lines whose amounts add up to the
 * original, so each part can be matched to its own PO/SO line or charge.
 */
export function SplitLineItemDialog({
  invoiceId,
  lineItem,
  currency,
  open,
  onOpenChange,
}: {
  invoiceId: string;
  lineItem: SplitLineItemTarget;
  currency: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        {open && (
          <SplitLineItemBody
            invoiceId={invoiceId}
            lineItem={lineItem}
            currency={currency}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SplitLineItemBody({
  invoiceId,
  lineItem,
  currency,
  onClose,
}: {
  invoiceId: string;
  lineItem: SplitLineItemTarget;
  currency: string;
  onClose: () => void;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const splitLineItem = useSplitAPInvoiceLineItem();
  const [parts, setParts] = useState<SplitPartDraft[]>(() =>
    initialSplitParts(lineItem),
  );
  const assessment = assessSplit(lineItem.totalPrice, parts);
  const money = (value: string | null | undefined) =>
    formatCurrency(value == null ? null : Number(value), { currency });

  function updatePart(
    key: string,
    field: 'quantity' | 'totalPrice',
    value: string,
  ) {
    setParts((current) =>
      current.map((part) =>
        part.key === key ? { ...part, [field]: value } : part,
      ),
    );
  }

  function handleSubmit() {
    splitLineItem.mutate(
      {
        apInvoiceId: invoiceId,
        lineItemId: lineItem.id,
        parts: toSplitPayload(parts),
      },
      {
        onSuccess: ({ lineItems }) => {
          showToast({
            message: gt('Line split into {count} lines', {
              count: lineItems.length,
              context: 'toast after splitting an AP invoice line',
            }),
            color: 'success',
          });
          onClose();
        },
        onError: (error) => {
          showToast({ message: error.message, color: 'error' });
        },
      },
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <T context="dialog title: split one AP invoice line into several">
            Split Line <Var>{lineItem.lineNumber}</Var>
          </T>
        </DialogTitle>
        <DialogDescription>
          <T context="split line dialog description">
            <Var>{lineItem.description || ''}</Var> ·{' '}
            <Var>{money(lineItem.totalPrice)}</Var>. The parts must add up to
            the line total. Each part becomes its own line you can match, and
            any existing match on this line is removed.
          </T>
        </DialogDescription>
      </DialogHeader>

      <div className="max-h-[50vh] overflow-y-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <T context="split line dialog column: part number">#</T>
              </TableHead>
              <TableHead className="w-28">
                <T context="split line dialog column: quantity">Qty</T>
              </TableHead>
              <TableHead>
                <T context="split line dialog column: amount">Amount</T>
              </TableHead>
              <TableHead className="text-right">
                <T context="split line dialog column: derived unit price">
                  Unit Price
                </T>
              </TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {parts.map((part, index) => (
              <TableRow key={part.key}>
                <TableCell className="text-muted-foreground">
                  {index + 1}
                </TableCell>
                <TableCell>
                  <Input
                    aria-label={gt('Part {n} quantity', {
                      n: index + 1,
                      context: 'split line dialog: quantity input label',
                    })}
                    inputMode="decimal"
                    value={part.quantity}
                    onChange={(e) =>
                      updatePart(part.key, 'quantity', e.target.value)
                    }
                    className="h-8"
                  />
                </TableCell>
                <TableCell>
                  <Input
                    aria-label={gt('Part {n} amount', {
                      n: index + 1,
                      context: 'split line dialog: amount input label',
                    })}
                    inputMode="decimal"
                    value={part.totalPrice}
                    onChange={(e) =>
                      updatePart(part.key, 'totalPrice', e.target.value)
                    }
                    className="h-8"
                  />
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {money(unitPricePreview(part))}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={parts.length <= 2}
                    aria-label={gt('Remove part {n}', {
                      n: index + 1,
                      context: 'split line dialog: remove part button',
                    })}
                    onClick={() =>
                      setParts((current) =>
                        current.filter((other) => other.key !== part.key),
                      )
                    }
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between text-sm">
        <Button
          variant="outline"
          size="sm"
          disabled={!canAddSplitPart(parts)}
          onClick={() =>
            setParts((current) =>
              appendSplitPart(
                lineItem.totalPrice,
                current,
                current[0]?.quantity ?? '1',
              ),
            )
          }
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          <T context="split line dialog: add another part">Add Part</T>
        </Button>
        <div className="text-right">
          <div>
            <T context="split line dialog: sum of the part amounts">
              Parts total: <Var>{money(assessment.partsTotal)}</Var>
            </T>
          </div>
          <div
            className={cn(
              'text-xs',
              assessment.remaining === '0.00'
                ? 'text-muted-foreground'
                : 'text-destructive',
            )}
          >
            <T context="split line dialog: amount not yet assigned to a part">
              Unassigned: <Var>{money(assessment.remaining)}</Var>
            </T>
          </div>
          {assessment.hasOppositeSignPart && (
            <div className="text-xs text-destructive">
              {Number(lineItem.totalPrice) < 0 ? (
                <T context="split line dialog: a part of a credit line is positive">
                  Each part of a credit line must be negative.
                </T>
              ) : (
                <T context="split line dialog: a part is negative or would cancel another part out">
                  Each part must be a positive amount.
                </T>
              )}
            </div>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          <T context="split line dialog: cancel button">Cancel</T>
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!assessment.canSubmit || splitLineItem.isPending}
        >
          <ScissorsIcon className="h-4 w-4 mr-1" />
          <T context="split line dialog: confirm button">Split Line</T>
        </Button>
      </DialogFooter>
    </>
  );
}
